import { API_BASE } from '../config/apiBase';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { 
  PlusCircle, 
  User,
  UserCheck,
  UserX,
  Shield,
  Briefcase,
  FileText,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = API_BASE;

const Users = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    role: 'analyst',
    training_language: 'es'
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.full_name) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success('Usuario creado exitosamente');
      setDialogOpen(false);
      setNewUser({ email: '', full_name: '', role: 'analyst', training_language: 'es' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/toggle-active`);
      toast.success('Estado actualizado');
      fetchUsers();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const getRoleIcon = (role) => {
    const icons = {
      admin: <Shield className="w-4 h-4" />,
      manager: <Briefcase className="w-4 h-4" />,
      analyst: <FileText className="w-4 h-4" />
    };
    return icons[role] || <User className="w-4 h-4" />;
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      manager: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      analyst: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
    };
    
    return (
      <Badge variant="outline" className={cn('font-medium', styles[role])}>
        {t(`role_${role}`)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="users-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">{t('user_management')}</h1>
          <p className="text-muted-foreground">{users.length} usuarios registrados</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="font-semibold" data-testid="create-user-btn">
              <PlusCircle className="w-4 h-4 mr-2" />
              {t('create_user')}
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">{t('create_user')}</DialogTitle>
              <DialogDescription>
                Ingrese los datos del nuevo usuario
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">{t('full_name')}</Label>
                <Input
                  id="full_name"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  placeholder="Juan Pérez"
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">{t('user_role')}</Label>
                <Select 
                  value={newUser.role} 
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trainee">{t('role_trainee') || 'Trainee'}</SelectItem>
                    <SelectItem value="analyst">{t('role_analyst')}</SelectItem>
                    <SelectItem value="manager">{t('role_manager')}</SelectItem>
                    <SelectItem value="admin">{t('role_admin')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Language selector - only shown for trainee role */}
              {newUser.role === 'trainee' && (
                <div className="space-y-2">
                  <Label htmlFor="training_language">{t('training_language') || 'Idioma de Entrenamiento'}</Label>
                  <Select 
                    value={newUser.training_language} 
                    onValueChange={(value) => setNewUser({ ...newUser, training_language: value })}
                  >
                    <SelectTrigger data-testid="user-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="es">Español</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t('training_language_hint') || 'El contenido de entrenamiento se mostrará en este idioma.'}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleCreateUser} 
                disabled={creating}
                data-testid="confirm-create-user"
              >
                {creating ? t('loading') : t('create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users List */}
      <div className="space-y-3">
        {users.map((user) => (
          <Card 
            key={user.id}
            className="hover:shadow-md transition-all"
            data-testid={`user-card-${user.id}`}
          >
            <CardContent className="p-4 lg:p-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={cn(
                    "w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0",
                    user.is_active ? "bg-primary/10" : "bg-muted"
                  )}>
                    {getRoleIcon(user.role)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold truncate">{user.full_name}</p>
                      {!user.is_active && (
                        <Badge variant="outline" className="text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                          Inactivo
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {getRoleBadge(user.role)}
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleToggleActive(user.id)}
                    title={user.is_active ? 'Desactivar' : 'Activar'}
                    data-testid={`toggle-user-${user.id}`}
                  >
                    {user.is_active ? (
                      <UserX className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <UserCheck className="w-4 h-4 text-emerald-600" />
                    )}
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteUser(user.id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-user-${user.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Users;
